import {
  Alert,
  Button,
  Checkbox,
  EAlertVariant,
  EButtonColorVariant,
  EHelpTextVariant,
  ELinkVariant,
  ETextInputTypes,
  EToastVariant,
  HelpText,
  Link as InlineLink,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import TermsLink from '@/components/ui/DisclaimerLinks/TermsLink';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import {
  emailValidator,
  setPasswordValidator,
  termsValidator,
  usePhoneValidatorBuilder,
} from '@/constants/validationRules';
import { useFullNameInputRules } from '@/hooks/useInputRules';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { clearAuthError, selectAuth, setJustLoggedIn } from '@/redux/modules/auth';
import { IUserResponse } from '@/redux/modules/auth/userMini';
import apiRequest from '@/services/apiRequest';
import { ILoginResponse } from '@/services/types/auth/ILoginResponse';
import { ISignupForm } from '@/services/types/auth/ISignupForm';
import { getCoreApi } from '@/utility/getCoreApi';
import { getClientLocale } from '@/utility/helpers';
import { logger } from '@/utility/logger';
import { navigateTo } from '@/utility/navigateTo';
import { setSessionCookie } from '@/utility/session.login';
import { triggerCustomEvent } from '@/utility/triggerCustomEvent';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

import PrivacyLink from '../ui/DisclaimerLinks/PrivacyLink';
import { getAuthErrorCode, getAuthErrorMessage, isNameRejectionCode } from './getAuthErrorMessage';
import { TFieldErrors } from './types/TFieldErrors';

export type IProps = {
  onSignup: (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    country?: string;
    token?: string;
    enableSms: boolean;
  }) => void;
  /**
   * Deprecated: previously used to show a global error banner above the form.
   * Kept for backwards compatibility; no longer rendered.
   */
  error?: { error?: string } | string | null;
  reCaptchaToken: string;
  onDidAcceptTerms?: (termsState: boolean) => void;
  signupVerification?: {
    needsVerification: boolean;
    signupResponse: ILoginResponse | null;
  };
};

const SignUpForm: React.FC<IProps> = ({
  onSignup,
  reCaptchaToken,
  onDidAcceptTerms,
  signupVerification,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();
  // Read the signup-scoped error (not the shared `error`) so an unrelated fetchUser/login
  // failure can't surface on the signup form.
  const signupError = useSelector(selectAuth).signupError;
  const { showToast } = useToast();
  const buildPhoneValidator = usePhoneValidatorBuilder();
  const buildFullNameInputRules = useFullNameInputRules();

  // Start each visit to the form with a clean slate (no stale error from a prior attempt).
  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);
  const recaptchaRef: MutableRefObject<ReCAPTCHA | null> = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [isClient, setIsClient] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors, isSubmitted },
  } = useForm<ISignupForm & { terms: boolean }>({
    defaultValues: {
      enableSms: false,
      terms: true,
    },
  });
  const didCheckTerms = watch('terms');
  const shouldUseRecaptcha = !!reCaptchaToken;

  const handleFieldFocus = useCallback((event: React.FocusEvent<HTMLElement>) => {
    // Ensure the focused field is visible without requiring manual scrolling
    try {
      event.currentTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    } catch {
      // Fail silently if scrollIntoView is not available or throws
    }
  }, []);

  useEffect(() => {
    onDidAcceptTerms?.(!!didCheckTerms);
  }, [didCheckTerms, onDidAcceptTerms]);

  // Set isClient to true after mount to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  const reset = () => {
    setIsSubmitting(false);
    recaptchaRef.current?.reset();
  };

  // Signup has no stored number to interpret — PhoneNumberInput submits E.164.
  const phoneValidator = buildPhoneValidator();

  // 'name' is excluded: its rules carry their own messages now (the two approved
  // Latin-script strings plus length/required), so there is nothing to collapse.
  const fieldErrors: TFieldErrors<Exclude<keyof ISignupForm | 'terms', 'phone' | 'name'>> = {
    terms: {
      required: intl.formatMessage({
        defaultMessage: 'You must accept the Outdoorsy Terms of Service.',
        id: 'MGUg/m',
      }),
    },
    password: {
      required: intl.formatMessage({
        defaultMessage: 'Password is required.',
        id: 'pRvgsc',
      }),
      minLength: intl.formatMessage({
        defaultMessage: 'Password must be at least 8 characters.',
        id: '1JjZZ5',
      }),
      maxLength: intl.formatMessage({
        defaultMessage: 'Password must be at most 50 characters.',
        id: 'quRrnb',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Password must contain at least one number.',
        id: 'dOPV08',
      }),
    },
    email: {
      required: intl.formatMessage({
        defaultMessage: 'Email is required',
        id: 'cU/aSG',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'xs/MJu',
      }),
    },
    enableSms: {},
  };

  const messageInputEmail = intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' });
  const messageInputName = intl.formatMessage({ defaultMessage: 'Full name', id: 'yk4PT9' });
  const messageNameRequired = intl.formatMessage({
    defaultMessage: 'Full name is required',
    id: 'MSbK0Z',
  });
  const messageInputPassword = intl.formatMessage({ defaultMessage: 'Password', id: '5sg7KC' });
  const messageInputPhone = intl.formatMessage({ defaultMessage: 'Phone number', id: 'jdJhOL' });
  const messageInputSubmit = intl.formatMessage({
    defaultMessage: 'Create your account',
    id: 'SeO7GZ',
  });
  const showPasswordLabel = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'JHTwdB',
  });

  const messageConfirm = intl.formatMessage(
    {
      defaultMessage:
        'I certify that I’m at least 18 years old at the time of sign up and agree to the <terms>Outdoorsy Terms of Service</terms>.',
      id: 'jl7p/x',
    },
    { terms: (label: React.ReactNode) => <TermsLink>{label}</TermsLink> },
  );

  const messageConfirmPhoneNotifications = intl.formatMessage(
    {
      defaultMessage:
        'I agree to receive phone and SMS communications from The Ride Platform to the phone number provided. Msg frequency varies. Msg&data rates may apply. Reply STOP to opt out, HELP for help. <privacy>View Privacy Policy here</privacy>.',
      description: 'Signup > SMS consent checkbox label',
      id: 'abU/Tr',
    },
    {
      privacy: (label: React.ReactNode) => <PrivacyLink>{label}</PrivacyLink>,
    },
  );

  const onSubmit = useCallback<(data: ISignupForm & { terms: boolean }) => void>(
    values => {
      setIsSubmitting(true);
      (async () => {
        try {
          const token = (shouldUseRecaptcha && (await recaptchaRef.current?.executeAsync())) || '';
          const locale = getClientLocale();
          await onSignup({
            name: values.name,
            email: values.email,
            phone: values.phone,
            password: values.password,
            country: locale?.country,
            token,
            enableSms: values.enableSms,
          });

          if (router.pathname === '/become-a-host') {
            navigateTo(`${process.env.hostApp}/list-your-rv`);
          }
        } catch (exception) {
          logger.error(exception);
        } finally {
          reset();
        }
      })();
    },
    [onSignup, shouldUseRecaptcha, router.pathname],
  );

  // Server-side error (e.g. email already taken) mapped to friendly, actionable copy.
  const errorDisplay = getAuthErrorMessage(intl, signupError);
  const isServerNameRejection = isNameRejectionCode(getAuthErrorCode(signupError));
  const serverNameMessage = isServerNameRejection ? errorDisplay?.message : undefined;

  const nameErr = errors.name?.message;
  const emailErr = errors.email && fieldErrors.email[errors.email.type];
  // usePhoneValidatorBuilder carries its own messages, so this reads them off the error.
  const phoneErr = errors.phone?.message;
  const passErr = errors.password && fieldErrors.password[errors.password.type];
  const termsErr = errors.terms && fieldErrors.terms[errors.terms.type];

  // A name rejection from the server belongs on the field, not in the banner above
  // the form — it is about this input, and the guest has to edit it to proceed.
  //
  // Routed through RHF rather than read straight off redux, so it clears itself
  // the moment the guest fixes the name (reValidateMode defaults to 'onChange'
  // after the first submit). `signupError` only clears on SIGNUP_USER_PENDING or
  // CLEAR_AUTH_ERROR, so rendering from it directly would pin a stale
  // Latin-script error under a field that is already correct, until the next
  // submit. The other four surfaces use setError; this keeps signup consistent.
  useEffect(() => {
    // A server rejection can only belong to a submit this instance made.
    // SignUpModal renders Modal without keepMounted, so closing it unmounts the
    // form while signupError lives on in redux — and the mount-time
    // clearAuthError() above is batched, so this effect's first run still sees the
    // stale error. Setting from that would pin a message on a blank form that no
    // validation pass can clear, since reValidateMode doesn't engage until
    // isSubmitted. Guarding on isSubmitted is what keeps a reopened modal clean.
    if (!isSubmitted) return;

    if (isNameRejectionCode(getAuthErrorCode(signupError))) {
      setError('name', { type: 'server', message: serverNameMessage });
    }
    // serverNameMessage is derived from signupError; keying on both would re-fire
    // on every intl change without adding anything. The cost is that a locale
    // switch between rejection and re-render leaves this message in the old
    // language until the next submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupError, setError, isSubmitted]);

  // Check if we need to show email verification content
  // Only show verification UI after client-side hydration to prevent SSR mismatch
  const needsEmailVerification = isClient && (signupVerification?.needsVerification || false);
  const signupResponse = signupVerification?.signupResponse;
  const verificationToken = signupResponse?.token;

  // Auto-send verification email when needsEmailVerification becomes true
  useEffect(() => {
    if (needsEmailVerification && countdown === 0 && verificationToken) {
      // Automatically send verification email and start countdown
      sendVerificationEmail();
    }
  }, [needsEmailVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Polling effect for email verification
  useEffect(() => {
    if (!needsEmailVerification || !verificationToken) return;

    let pollCount = 0;
    const maxPolls = 120; // 120 polls * 5 seconds = 10 minutes
    let isCancelled = false;

    const pollVerification = async () => {
      try {
        const user = await apiRequest<IUserResponse>(
          {
            url: `${getCoreApi()}/users/mini_me`,
            method: 'GET',
          },
          true,
          false,
          verificationToken,
        );

        // Check cancellation flag after async operation completes
        if (isCancelled) return;

        if (user?.verifications?.email === true) {
          // Email is verified! Complete the signup
          if (signupResponse) {
            dispatch(setJustLoggedIn());
            await setSessionCookie(signupResponse, dispatch);
            triggerCustomEvent('session', 'load-user');
          }
          return; // Stop polling
        }

        pollCount++;
        if (pollCount < maxPolls && !isCancelled) {
          pollingTimeoutRef.current = setTimeout(pollVerification, 5000);
        }
      } catch {
        // Check cancellation flag before continuing
        if (isCancelled) return;

        // Continue polling even on error
        pollCount++;
        if (pollCount < maxPolls && !isCancelled) {
          pollingTimeoutRef.current = setTimeout(pollVerification, 5000);
        }
      }
    };

    // Start polling
    pollVerification();

    // Cleanup on unmount
    return () => {
      isCancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [needsEmailVerification, verificationToken, signupResponse, dispatch]);

  // Resend verification email
  const sendVerificationEmail = async () => {
    if (!verificationToken) {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          id: 'Rqh+eJ',
          defaultMessage: 'Unable to resend verification email. Please try again.',
        }),
      );
      return;
    }

    // Start countdown immediately to avoid showing "Click here to resend" during network call
    setCountdown(60);
    setIsSendingEmail(true);

    try {
      await apiRequest(
        {
          url: `${getCoreApi()}/auth/creds/resend`,
          method: 'POST',
        },
        true,
        false,
        verificationToken,
      );

      setIsSendingEmail(false);
      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: "We've sent a verification email.",
          id: '+i/ZIw',
        }),
      );
    } catch {
      setIsSendingEmail(false);
      // Don't reset countdown on error - let it continue
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Oops, something went wrong. Please, try again later.',
          id: 'uSQB3M',
        }),
      );
    }
  };

  // If email verification is needed, show the verification content instead of form
  if (needsEmailVerification) {
    return (
      <div className="space-y-4">
        <Alert
          title={intl.formatMessage({
            id: 'MyAVtk',
            defaultMessage: 'Please verify your email address',
            description: 'Sign Up Form > Email verification info alert title',
          })}
          content={intl.formatMessage(
            {
              id: '/uws+f',
              defaultMessage: `Please check your email inbox and follow the link in the email we've sent. Return here after you have verified your email. {question} {resendLink}`,
              description: 'Sign Up Form > Email verification info alert message',
            },
            {
              question:
                countdown === 0 ? (
                  <FormattedMessage defaultMessage="Didn't receive email?" id="lrclxq" />
                ) : undefined,
              resendLink:
                countdown === 0 ? (
                  <InlineLink
                    variant={ELinkVariant.Underline}
                    onClick={sendVerificationEmail}
                    disabled={isSendingEmail}>
                    <FormattedMessage
                      id="V3lIOV"
                      defaultMessage="Click here to resend."
                      description="Sign Up Form > Resend verification email link"
                    />
                  </InlineLink>
                ) : (
                  <span>
                    {intl.formatMessage(
                      {
                        id: 'MK11Wv',
                        defaultMessage: 'Please wait {seconds} seconds before retrying.',
                      },
                      { seconds: countdown },
                    )}
                  </span>
                ),
            },
          )}
          variant={EAlertVariant.Info}
        />
      </div>
    );
  }

  return (
    <form
      className="text-sm space-y-6"
      aria-label={messageInputSubmit}
      onSubmit={handleSubmit(onSubmit)}
      noValidate>
      {errorDisplay && !isServerNameRejection && (
        <div className="space-y-1" role="alert">
          <HelpText text={errorDisplay.message} variant={EHelpTextVariant.Error} showIcon />
          {errorDisplay.showLoginCta && (
            <InlineLink variant={ELinkVariant.Underline} onClick={() => triggerLoginModal(0)}>
              <FormattedMessage
                id="Yc6hqW"
                defaultMessage="Log in to your existing account"
                description="Sign Up Form > Duplicate email error > Log in instead CTA"
              />
            </InlineLink>
          )}
        </div>
      )}
      <fieldset className="flex flex-col gap-y-6">
        <div className="grid grid-cols-1 gap-y-5 gap-x-4 md:grid-cols-2">
          <Controller
            name="name"
            control={control}
            rules={buildFullNameInputRules({
              requiredMessage: messageNameRequired,
              minLengthMessage: messageNameRequired,
              maxLengthMessage: intl.formatMessage({
                defaultMessage: 'Full name is too long.',
                id: '8ZPdzW',
              }),
              lastNameMessage: messageNameRequired,
            })}
            render={({ field }) => (
              <TextInput
                controlled
                name="name"
                autoComplete="name"
                label={messageInputName}
                ariaLabel={messageInputName}
                inputValue={field.value || ''}
                onChange={field.onChange}
                onFocus={handleFieldFocus}
                markAsRequired
                error={!!nameErr}
                helpText={nameErr}
                aria-required="true"
                aria-autocomplete="inline"
                aria-invalid={!!nameErr}
                aria-errormessage={nameErr}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={emailValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="email"
                autoComplete="email"
                label={messageInputEmail}
                ariaLabel={messageInputEmail}
                inputValue={field.value || ''}
                onChange={field.onChange}
                onFocus={handleFieldFocus}
                markAsRequired
                error={!!emailErr}
                helpText={emailErr}
                aria-required="true"
                aria-autocomplete="inline"
                aria-invalid={!!errors.email}
                aria-errormessage={emailErr}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={phoneValidator}
            render={({ field }) => (
              <PhoneNumberInput
                name="phone"
                // The country dropdown plus the number need the full row — half of the
                // two-column grid leaves the number itself too narrow to read.
                className="md:col-span-2"
                label={messageInputPhone}
                value={field.value || ''}
                onChange={field.onChange}
                onFocus={handleFieldFocus}
                markAsRequired
                error={!!phoneErr}
                helpText={phoneErr}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={setPasswordValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="password"
                className="md:col-span-2"
                autoComplete="new-password"
                label={messageInputPassword}
                ariaLabel={messageInputPassword}
                inputValue={field.value || ''}
                onChange={field.onChange}
                onFocus={handleFieldFocus}
                markAsRequired
                error={!!passErr}
                helpText={passErr}
                aria-required="true"
                aria-autocomplete="list"
                aria-invalid={!!errors.password}
                aria-errormessage={passErr}
                type={ETextInputTypes.Password}
                showPasswordButtonAriaLabel={showPasswordLabel}
              />
            )}
          />
        </div>
        <Controller
          name="enableSms"
          control={control}
          render={({ field }) => (
            <Checkbox
              isSelected={!!field.value}
              value="enableSms"
              ariaLabel={intl.formatMessage({
                defaultMessage:
                  'I agree to receive phone and SMS communications from The Ride Platform to the phone number provided. Msg frequency varies. Msg&data rates may apply. Reply STOP to opt out, HELP for help. View Privacy Policy here.',
                description: 'Signup > SMS consent checkbox aria label',
                id: 'B60owZ',
              })}
              disabled={isSubmitting}
              content={{
                label: <span>{messageConfirmPhoneNotifications}</span>,
              }}
              onChange={field.onChange}
              data-testid="terms-enable-sms-confirmation"
            />
          )}
        />
        <Controller
          name="terms"
          control={control}
          rules={termsValidator}
          render={({ field }) => (
            <Checkbox
              value="terms"
              isSelected={!!field.value}
              ariaLabel={intl.formatMessage({
                defaultMessage:
                  'I certify that I’m at least 18 years old at the time of sign up and agree to the Outdoorsy Terms of Service.',
                id: 'xMa0Hu',
              })}
              error={!!termsErr}
              disabled={isSubmitting}
              markAsRequired
              content={{
                label: <span>{messageConfirm}</span>,
              }}
              onChange={field.onChange}
              data-testid="terms-confirmation"
            />
          )}
        />
        <p className="text-gray-500">
          {intl.formatMessage({
            defaultMessage:
              'By signing up, you agree to receive marketing emails from The Ride Platform.',
            description: 'Signup > Marketing email consent disclaimer',
            id: 'Q97dL5',
          })}
        </p>
      </fieldset>
      <Button
        type="submit"
        data-testid="signup-submit-btn"
        aria-disabled="false"
        variant={EButtonColorVariant.Primary}
        label={messageInputSubmit}
        disabled={isSubmitting || !didCheckTerms}
        loading={isSubmitting}
        fullWidth
      />
      {shouldUseRecaptcha && isClient && (
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={reCaptchaToken} />
      )}
    </form>
  );
};

export { SignUpForm };
